/**
 * Насколько верить данным: пороги тревоги.
 *
 * ПОРОГ СЧИТАЕТСЯ ОТ ПЕРИОДА ИСТОЧНИКА, А НЕ ОБЩИЙ НА ВСЕХ. Это
 * исправление, и ошибка была из тех, что портят всё вокруг себя.
 * Пороги стояли общие — жёлтая старше двух часов, красная старше шести.
 * Для статистики, которая обновляется каждые полчаса, это верно.
 * А кампании выгружаются РАЗ В ШЕСТЬ ЧАСОВ: плашка желтела через два
 * часа после каждой выгрузки и краснела к концу цикла — при полностью
 * исправной работе.
 *
 * ПОСТОЯННАЯ ТРЕВОГА ХУЖЕ ОТСУТСТВИЯ ТРЕВОГИ. К красной полосе, которая
 * висит всегда, привыкают за день и перестают читать — а вместе с ней
 * перестают читать и ту, что однажды окажется настоящей.
 *
 * ПОЛТОРА ПЕРИОДА — ЖЁЛТАЯ, ТРИ — КРАСНАЯ. Полтора означает «одну
 * выгрузку пропустили», три — «пропустили две подряд, это уже не
 * случайность». Пороги из ТЗ 13.3 остаются нижней границей: чаще, чем
 * раз в два часа, тревожить незачем даже у самого частого источника.
 */

/** Ниже этого не тревожим никогда — ТЗ 13.3. */
export const MIN_YELLOW = 120;
export const MIN_RED = 360;

export type Level = 'ok' | 'warn' | 'bad';

export interface Source {
  kind: string;
  status: string;
  /** Как часто источник обновляется. `null` — расписания нет вовсе. */
  everyMinutes?: number | null;
  ageMinutes: number | null;
  complete: boolean;
  note: string | null;
}

export function thresholds(everyMinutes: number | null | undefined): {
  yellow: number;
  red: number;
} {
  const period = typeof everyMinutes === 'number' && everyMinutes > 0 ? everyMinutes : 0;
  return {
    yellow: Math.max(MIN_YELLOW, Math.round(period * 1.5)),
    red: Math.max(MIN_RED, period * 3),
  };
}

/** Уровень тревоги по одному источнику. */
export function levelOf(s: Source): Level {
  if (s.status === 'never') return 'bad';
  // НЕПОЛНАЯ ВЫГРУЗКА КРАСНЕЕ ЛЮБОГО ВОЗРАСТА: свежие данные, в которых
  // не хватает половины, опаснее вчерашних полных. По вчерашним человек
  // примет поправку в уме, по неполным — нет, они выглядят целыми.
  if (!s.complete && s.status !== 'running') return 'bad';
  if (s.ageMinutes === null) return 'ok';

  const t = thresholds(s.everyMinutes);
  if (s.ageMinutes > t.red) return 'bad';
  if (s.ageMinutes > t.yellow) return 'warn';
  return 'ok';
}

export function rank(l: Level): number {
  return l === 'bad' ? 2 : l === 'warn' ? 1 : 0;
}

/** Худший из источников — им и красится полоса. */
export function worstLevel(sources: readonly Source[]): Level {
  let out: Level = 'ok';
  for (const s of sources) if (rank(levelOf(s)) > rank(out)) out = levelOf(s);
  return out;
}

/**
 * Можно ли заводить новый тест ставок (ТЗ 13.3).
 *
 * ═══════════════════════════════════════════════════════════════════════
 * «ЗАПРЕТ НОВЫХ ТЕСТОВ ПРИ КРАСНОЙ ПЛАШКЕ» — И ЗАПРЕЩАЕТ СЕРВЕР.
 *
 * Лестничный тест ставок решает по замеру: держим ставку трое суток,
 * смотрим заказы и расход, идём на следующую ступень. Замер берётся
 * из выгруженной статистики.
 *
 * Красная плашка означает ровно одно: статистике сейчас верить нельзя —
 * либо она устарела втрое против расписания, либо пришла неполной.
 * Тест, запущенный поверх такой статистики, не измеряет ничего: он
 * СРАВНИВАЕТ ВЧЕРАШНЕЕ С ПОЗАВЧЕРАШНИМ и по итогам двигает настоящую
 * ставку в настоящем кабинете. Это трата чужих денег по случайному
 * числу.
 *
 * ЖЁЛТАЯ НЕ ЗАПРЕЩАЕТ. Жёлтая — «одну выгрузку пропустили»: замер
 * сдвинется на несколько часов и всё. Запрещать по ней значило бы
 * запрещать половину суток каждые сутки, и запрет перестали бы
 * замечать вместе с плашкой.
 *
 * ИДУЩИЕ ТЕСТЫ НЕ ТРОГАЕМ. Оборвать тест на середине вилки — значит
 * оставить кампанию на ставке, которую никто не выбирал. Запрет
 * касается только НОВЫХ: остановить идущий человек может сам.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function testsAllowed(sources: readonly Source[]): { ok: boolean; why: string | null } {
  if (worstLevel(sources) !== 'bad') return { ok: true, why: null };

  return {
    ok: false,
    why:
      'Данным кабинета сейчас нельзя верить: выгрузка устарела или пришла неполной. ' +
      'Тест ставок решает по замеру, а замерять нечем — он двигал бы настоящую ставку ' +
      'по случайному числу. Нажмите «Обновить» и попробуйте снова, когда полоса ' +
      'над экраном перестанет быть красной.',
  };
}
